(() => {
  const METHOD_SELECTOR = '[data-embe-product-details-method]';
  const FLIP_SELECTOR = '[data-embe-flip-card]';
  const FAQ_LIST_SELECTOR = '[data-embe-faq-list]';

  function syncMethodText(swiper, card) {
    const textPanels = card?.querySelectorAll?.('[data-method-text]');
    if (!textPanels?.length) return;

    const idx = swiper.realIndex;
    let matched = false;

    textPanels.forEach((panel) => {
      const panelIdx = Number.parseInt(panel.dataset.slideIndex, 10);
      const isActive = panelIdx === idx;
      panel.classList.toggle('is-active', isActive);
      panel.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      if (isActive) matched = true;
    });

    if (!matched) {
      textPanels.forEach((panel) => panel.classList.remove('is-active'));
    }
  }

  function initOne(el) {
    if (el.dataset.embeInit === '1') return;
    el.dataset.embeInit = '1';

    if (!window.Swiper) {
      let tries = 0;
      const timer = setInterval(() => {
        tries += 1;
        if (window.Swiper) {
          clearInterval(timer);
          el.dataset.embeInit = '0';
          initOne(el);
          return;
        }
        if (tries >= 30) clearInterval(timer);
      }, 100);
      return;
    }

    const card = el.closest('[data-embe-product-details-method-card]');
    const nextBtn = card?.querySelector('[data-embe-product-details-next]');
    const pagination = el.querySelector('.embe-product-details__method-pagination');
    const slideCount = el.querySelectorAll('.swiper-slide').length;

    const opts = {
      slidesPerView: 1,
      spaceBetween: 0,
      speed: 450,
      roundLengths: true,
      watchOverflow: true,
      observer: true,
      observeParents: true,
      loop: slideCount > 1,
      on: {
        init(swiper) {
          syncMethodText(swiper, card);
        },
        slideChange(swiper) {
          syncMethodText(swiper, card);
        },
      },
    };

    if (pagination && slideCount > 1) {
      opts.pagination = { el: pagination, clickable: true };
    }
    if (nextBtn && slideCount > 1) {
      opts.navigation = { nextEl: nextBtn };
    }

    try {
      // eslint-disable-next-line no-new
      new window.Swiper(el, opts);
    } catch (e) {
      el.classList.add('embe-swiper-failed');
    }
  }

  const FAQ_PANEL_TRANSITION = 'max-height 0.4s cubic-bezier(0.33, 1, 0.68, 1)';

  function prefersReducedFaqMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function setFaqItemState(item, trigger, panel, isOpen) {
    item.classList.toggle('is-open', isOpen);
    trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    if (panel) panel.setAttribute('aria-hidden', isOpen ? 'false' : 'true');
  }

  function getFaqPanelHeight(panel) {
    const inner = panel?.querySelector('.embe-product-details__faq-answer-inner');
    return inner ? inner.scrollHeight : 0;
  }

  function finishFaqPanelAnimation(item, panel) {
    item.classList.remove('is-animating');
    if (item.classList.contains('is-open')) {
      panel.style.maxHeight = 'none';
    } else {
      panel.style.maxHeight = '0';
    }
  }

  function animateFaqPanel(item, trigger, panel, isOpen) {
    if (!panel) {
      setFaqItemState(item, trigger, panel, isOpen);
      return;
    }

    if (prefersReducedFaqMotion()) {
      item.classList.remove('is-animating');
      setFaqItemState(item, trigger, panel, isOpen);
      panel.style.maxHeight = isOpen ? 'none' : '0';
      return;
    }

    item.classList.add('is-animating');
    setFaqItemState(item, trigger, panel, isOpen);

    if (isOpen) {
      const height = getFaqPanelHeight(panel);
      panel.style.maxHeight = '0';
      panel.style.transition = FAQ_PANEL_TRANSITION;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          panel.style.maxHeight = `${height}px`;
        });
      });
      return;
    }

    const height = panel.style.maxHeight === 'none' ? getFaqPanelHeight(panel) : panel.offsetHeight;
    panel.style.maxHeight = `${height}px`;
    panel.style.transition = FAQ_PANEL_TRANSITION;
    requestAnimationFrame(() => {
      panel.style.maxHeight = '0';
    });
  }

  function initFaqAccordions(root = document) {
    root.querySelectorAll?.(FAQ_LIST_SELECTOR)?.forEach?.((list) => {
      if (list.dataset.embeFaqInit === '1') return;
      list.dataset.embeFaqInit = '1';

      const items = list.querySelectorAll('[data-embe-faq-item]');

      items.forEach((item) => {
        const trigger = item.querySelector('[data-embe-faq-trigger]');
        const panel = item.querySelector('[data-embe-faq-panel]');
        if (!trigger) return;

        if (panel) {
          panel.style.maxHeight = '0';
          panel.addEventListener('transitionend', (event) => {
            if (event.target !== panel || event.propertyName !== 'max-height') return;
            finishFaqPanelAnimation(item, panel);
          });
        }

        trigger.addEventListener('click', () => {
          const isOpen = item.classList.contains('is-open');

          if (isOpen) {
            animateFaqPanel(item, trigger, panel, false);
            return;
          }

          items.forEach((other) => {
            if (other === item) return;
            const otherTrigger = other.querySelector('[data-embe-faq-trigger]');
            const otherPanel = other.querySelector('[data-embe-faq-panel]');
            if (!otherTrigger || !other.classList.contains('is-open')) return;
            animateFaqPanel(other, otherTrigger, otherPanel, false);
          });

          animateFaqPanel(item, trigger, panel, true);
        });
      });
    });
  }

  function initFlipCards(root = document) {
    const canHover = window.matchMedia('(hover: hover)').matches;

    root.querySelectorAll?.(FLIP_SELECTOR)?.forEach?.((card) => {
      if (card.dataset.embeFlipInit === '1') return;
      if (card.classList.contains('embe-product-details__flip-card--ingredients')) return;
      card.dataset.embeFlipInit = '1';

      const closeBtn = card.querySelector('[data-embe-flip-close]');

      closeBtn?.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        card.classList.remove('is-flipped');
      });

      if (!canHover) {
        card.addEventListener('click', (event) => {
          if (event.target.closest('[data-embe-flip-close]')) return;
          card.classList.toggle('is-flipped');
        });
      }
    });
  }

  function init(root = document) {
    root.querySelectorAll?.(METHOD_SELECTOR)?.forEach?.(initOne);
    initFlipCards(root);
    initFaqAccordions(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
